import React, { useMemo } from 'react';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Page,
  Sort,
  Selection,
  Inject,
} from '@syncfusion/ej2-react-grids';

import { useAppSelector } from '../../../../../app/store';
import { useCallLogSelection } from './hooks';
import {
  dateTimeTemplate,
  statusTemplate,
  notesTemplate,
  callerTemplate,
} from './templates';

export interface CallLogGridProps {
  onRowClick?: (callLog: any) => void;
  testId?: string;
}

/**
 * CallLogGrid - Displays call log entries for a case
 *
 * Follows the established grid pattern with:
 * - Separated templates for cell rendering
 * - Custom hooks for selection handling
 * - Clean component structure
 */
export const CallLogGrid: React.FC<CallLogGridProps> = ({ onRowClick, testId }) => {
  const { currentCase } = useAppSelector((state) => state.caseManage);
  const callLogs = currentCase?.callLogs || [];

  // Sort call logs by date (newest first)
  const sortedCallLogs = useMemo(
    () =>
      [...callLogs].sort(
        (a, b) => new Date(b.callDate).getTime() - new Date(a.callDate).getTime()
      ),
    [callLogs]
  );

  // Use custom hook for row selection
  const { handleRowSelected } = useCallLogSelection(onRowClick);

  return (
    <div style={{ width: '800px', margin: '0 auto' }}>
      <GridComponent
        dataSource={sortedCallLogs}
        allowPaging={true}
        allowSorting={true}
        allowSelection={true}
        selectionSettings={{ type: 'Single' }}
        rowSelected={handleRowSelected}
        pageSettings={{ pageSize: 10, pageSizes: [5, 10, 20] }}
        gridLines="Horizontal"
        height="400"
        rowHeight={60}
        data-testid={testId}
      >
        <ColumnsDirective>
          <ColumnDirective
            field="callDate"
            headerText="Date & Time"
            width="140"
            template={dateTimeTemplate}
            allowSorting={true}
          />
          <ColumnDirective
            field="callBy"
            headerText="Caller & Contact"
            width="180"
            template={callerTemplate}
          />
          <ColumnDirective
            field="isCallAnswered"
            headerText="Status"
            width="100"
            template={statusTemplate}
            textAlign="Center"
          />
          <ColumnDirective
            field="notes"
            headerText="Notes"
            width="380"
            template={notesTemplate}
          />
        </ColumnsDirective>
        <Inject services={[Page, Sort, Selection]} />
      </GridComponent>
    </div>
  );
};

export default CallLogGrid;
